import React from 'react'
import Highcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import CloseCircle from '../icons/CloseCircle'
import ContainerHeading from '../layout/ContainerHeading'
import HelmetTitle from '../../components/layout/HelmetTitle'
import Moment from 'moment-timezone'
import GraphActionsBar from './GraphActionsBar'
import styledTheme from '../../styles/theme'

import { DataViewModalContainer, ContainerHeadingActionIcon, GraphContentWrapper, GraphContainer } from './styled-elements'

export default class Graph extends React.Component {

  constructor(props) {
    super(props)
    const layoutWidth=this.getLayoutWdth()
    const layoutHeight=this.getLayoutHeight()
    const isSmallScreen=(layoutWidth < styledTheme.breakpoints.lg)
    const mobileLandscapeView=(isSmallScreen && (layoutWidth > layoutHeight))
    this.state = {
      layoutWidth,
      layoutHeight,
      isSmallScreen,
      mobileLandscapeView,
    }
    this.graphContainerDiv = React.createRef()
    this.highChartsChart = React.createRef()
  }

  getLayoutWdth = () => {
    if (typeof window === 'undefined') return styledTheme.breakpoints.lg
    return window.innerWidth
  }

  getLayoutHeight = () => {
    if (typeof window === 'undefined') return (styledTheme.breakpoints.lg*9/16)
    return window.innerHeight
  }

  formatSeries = (seriesArray) => seriesArray.map(series => {
    if (series.yAxis==='y_axis_percent' && series.offset_enabled) {
      return {
        ...series,
        tooltip: {
          pointFormatter: function() {
            return '<span style="color:' + this.color + '">\u25CF</span> ' + series.name + ' <b>' + (this.y+series.offset).toFixed(2) + '%</b><br/>'
          }
        }
      }
    }
    return series
  })

  processOptions = (options,props,state) => {
    const mobileDisplayOptions = {}
    if (state.layoutWidth < styledTheme.breakpoints.lg) {
      mobileDisplayOptions['scrollbar'] = { enabled: false }
      //mobileDisplayOptions['legend'] = { enabled: false }
      //mobileDisplayOptions['rangeSelector'] = { enabled: false }
      const xAxis = { ...options['xAxis'] }
      const maxUnixTimeMoment = Moment(new Date(xAxis[0]['max']))
      const monthAgoUnixtime = maxUnixTimeMoment.clone().subtract(1,'M')
      const xAxisRange = maxUnixTimeMoment.diff(monthAgoUnixtime)
      xAxis['range'] = xAxisRange
      xAxis['startOnTick'] = true
      xAxis['endOnTick'] = true
      xAxis['ordinal'] = false
      mobileDisplayOptions['xAxis'] = xAxis
    }
    options.chart['height'] = this.state.chartContainerHeight-20 || "100%";
    options.chart['width'] = this.state.chartContainerWidth-20 || 300;
    return {
      ...options,
      ...mobileDisplayOptions,
      series: this.formatSeries(options.series.slice()),
      time: {
        getTimezoneOffset: function (timestamp) {
          return -Moment.tz(timestamp, options.time.timezone).utcOffset()
        }
      }
    }
  }

  last3Heights = []
  last3Widths = []

  resizeHandler = (e) => {

    this.last3Heights = []
    this.last3Widths = []

    const resizeFunction = () => {
      if (e.target.innerWidth===this.state.layoutWidth) return
      const layoutWidth=this.getLayoutWdth()
      const layoutHeight=this.getLayoutHeight()
      const isSmallScreen=(layoutWidth < styledTheme.breakpoints.lg)
      const mobileLandscapeView=(isSmallScreen && (layoutWidth > layoutHeight))
      this.setState({
        chartContainerHeight: this.graphContainerDiv.current.offsetHeight,
        chartContainerWidth: this.graphContainerDiv.current.offsetWidth,
        layoutWidth,
        layoutHeight,
        isSmallScreen,
        mobileLandscapeView,
      })
    }

    // To handle animated screen re-sizing and delayed updating of window.innerWidth in Mobile Chrome:
    // Run this function 3 times, and only when the height and width have been stable for 3 seconds,
    // stop calling resizeFunction

    const intervalId = setInterval(() => {
      this.last3Widths.push(e.target.innerWidth)
      this.last3Heights.push(e.target.innerHeight)
      this.last3Widths = this.last3Widths.slice(-3)
      this.last3Heights = this.last3Heights.slice(-3)
      if ((this.last3Widths.length < 3) || (this.last3Heights.length < 3)) {
        resizeFunction()
      } else {
        const widthTotal = this.last3Widths.reduce((n,total) => total+n,0)
        const heightTotal = this.last3Heights.reduce((n,total) => total+n,0)
        if (((widthTotal/3)===this.last3Widths[2]) && ((heightTotal/3)===this.last3Heights[2])) {
          clearInterval(intervalId)
        } else {
          resizeFunction()
        }
      }
    },1000)
  }

  addDataPoint = (seriesId,dataPoint) => {
    const chart = this.highChartsChart.current.chart
    const series = chart.get(seriesId)
    series.addPoint(dataPoint)
    const xAxis = series.xAxis
    const yAxis = series.yAxis
    xAxis.setExtremes(Math.min(xAxis.min,dataPoint[0]),Math.max(xAxis.max,dataPoint[0]))
    yAxis.setExtremes(Math.min(yAxis.min,dataPoint[1]),Math.max(yAxis.max,dataPoint[1]))
  }

  componentDidMount = () => {
    this.removeHighchartsCredit.call(this)
    const container = this.highChartsChart.current.container.current;
    container.style.height = "100%";
    container.style.width = "100%";
    this.highChartsChart.current.chart.reflow();
    this.updateChartContainerDimensions()
    window.addEventListener('resize', this.resizeHandler)
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.resizeHandler)
  }

  componentDidUpdate = () => {
    this.updateChartContainerDimensions()
    if (this.highChartsChart.current.chart) {
      this.highChartsChart.current.chart.setSize(
        this.state.chartContainerWidth,
        this.state.chartContainerHeight,
      )
      this.highChartsChart.current.chart.reflow()
    }
  }

  updateChartContainerDimensions() {
    if (
      (this.graphContainerDiv) &&
      (this.graphContainerDiv.current) &&
      (
        (this.state.chartContainerHeight!==this.graphContainerDiv.current.offsetHeight) ||
        (this.state.chartContainerWidth!==this.graphContainerDiv.current.offsetWidth)
      )
    ) {
        this.setState({
          chartContainerHeight: this.graphContainerDiv.current.offsetHeight,
          chartContainerWidth: this.graphContainerDiv.current.offsetWidth,
        })
    }
  }

  removeHighchartsCredit = () => {
    const highchartsCreditElArray = document.querySelectorAll('.highcharts-credits')
    if (highchartsCreditElArray.length) {
      const highchartsCredit = highchartsCreditElArray[0]
      highchartsCredit.parentNode.removeChild(highchartsCredit)
    }
  }

  renderBody = (props,state) => {

    const { site, dataView, options, includeGraphActions } = props
    const processedOptions = this.processOptions(options,props,state)

    const HighchartsObj = <GraphContentWrapper>
      <GraphContainer innerRef={this.graphContainerDiv}>
        <HighchartsReact
          ref={this.highChartsChart}
          highcharts={Highcharts}
          constructorType={'stockChart'}
          options={processedOptions}
          allowChartUpdate={false}
        />
      </GraphContainer>
    </GraphContentWrapper>

    const title = site ? `${site.name}: ${dataView.name}` : dataView.name

    return <DataViewModalContainer>
      <HelmetTitle title={title} />
      {(state.mobileLandscapeView===false) && <ContainerHeading
        title={title}
        actionIcons={
          <ContainerHeadingActionIcon onClick={this.props.closeDataViewHandler}><CloseCircle /></ContainerHeadingActionIcon>
        }
      />}
      {(state.mobileLandscapeView===false) && includeGraphActions && <GraphActionsBar
        manualInputFormOpenHandler={props.manualInputFormOpenHandler}
      />}
      {HighchartsObj}
    </DataViewModalContainer>
  }

  render = () => this.renderBody(this.props,this.state)

}




// WEBPACK FOOTER //
// ./src/components/dataViews/Graph.js